import {makeAutoObservable, reaction, runInAction} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {ChannelCourseResource} from '../../../../api/channel';
import type {CollectionStore} from '../../../../api/utils/collectionStore';
import type {Nullable} from '../../../../utils/utility-types';
import type {Channel, ChannelVariant} from '../../types';

import {currentUserStore} from '../../../../api/user';
import {isChannelReadonly} from '../../../../utils/entity/channel';
import {findChannels} from '../../../../utils/entity/channel/findChannels';
import {isCourseStarted} from '../../../../utils/entity/course';
import {
  type Destructible,
  ReactionDisposeManager,
} from '../../../../utils/store';
import {isDefined} from '../../../../utils/typecheck';
import {createTextInputField} from '../../../../utils/validation';
import {ChannelsStoreFactory} from './ChannelsStoreFactory';

type Config = {
  isMultiselect?: boolean;
  selectedChannelIds?: string[];
  startedOnly?: boolean;
  tabs?: ChannelVariant[];
};

export namespace ShareToChannelStore {
  export type Section = {
    data: Channel[];
    isDisabled: boolean;
    title?: string;
  };
}

export class ShareToChannelStore implements Destructible {
  activeTab: ChannelVariant = 'course';
  messageField = this.makeMessageField();
  searchQuery = '';
  selectedChannelIds = new Map<string, Set<string>>();
  tabs: ChannelVariant[] = ['course', 'dm'];
  private channelStores = new Map<ChannelVariant, CollectionStore<Channel>>();
  private disabledChannelsIds = new Set<string>();
  private disposer = new ReactionDisposeManager();

  constructor(private readonly config?: Config) {
    const channelsStoreFactory = new ChannelsStoreFactory();

    this.tabs = this.config?.tabs || this.tabs;

    this.tabs.forEach(tab => {
      this.channelStores.set(
        tab,
        this.createStoreForTab(tab, channelsStoreFactory),
      );

      this.selectedChannelIds.set(tab, new Set());
    });

    this.activeTab = this.tabs[0]!;

    makeAutoObservable(this, {}, {autoBind: true});

    this.setupReactions();
  }

  destruct() {
    this.channelStores.forEach(store => store.clear());

    this.disposer.dispose();
  }

  get allChannelsCount() {
    return this.channels.length;
  }

  get disabledChannelsCount() {
    return this.disabledChannels.length;
  }

  get hasSelectedChannels() {
    return (this.getSelectionBytActiveTab()?.size || 0) > 0;
  }

  get isEmpty() {
    return this.isLoaded && this.allChannelsCount === 0;
  }

  get isLoaded() {
    return this.activeStore?.isLoaded ?? false;
  }

  get isSearchActive() {
    return this.shouldSearch;
  }

  get isSelectAllAllowed() {
    return this.config?.isMultiselect && this.selectableChannelsCount > 0;
  }

  get isSelectedAll() {
    return (
      this.selectableChannelsCount > 0 &&
      this.selectedChannels.length === this.selectableChannelsCount
    );
  }

  get sections(): ShareToChannelStore.Section[] {
    return [
      {
        data: this.selectableChannels,
        isDisabled: false,
      },
      this.disabledChannels.length > 0
        ? {
            data: this.disabledChannels,
            isDisabled: true,
          }
        : null,
    ].filter(Boolean);
  }

  get selectableChannelsCount() {
    return this.selectableChannels.length;
  }

  get selectedChannels() {
    const selectionByActiveTab = this.getSelectionBytActiveTab();

    if (!selectionByActiveTab) {
      return [];
    }

    return Array.from(selectionByActiveTab)
      .map(id => this.activeStore?.items.find(channel => channel._id === id))
      .filter(Boolean);
  }

  get selectionCount() {
    return this.getSelectionBytActiveTab()?.size || 0;
  }

  clearSearchQuery() {
    this.searchQuery = '';
  }

  clearSelection() {
    this.selectedChannelIds.clear();
  }

  fetch() {
    this.activeStore?.fetch();
  }

  isChannelSelected(channelId: string) {
    return this.getSelectionBytActiveTab()?.has(channelId) || false;
  }

  setActiveTab(tab: ChannelVariant) {
    this.activeTab = tab;
    this.clearSearchQuery();
  }

  setSearchQuery(query: string) {
    this.searchQuery = query;
  }

  toggleChannelSelection(channelId: string) {
    const selectionByActiveTab = this.getSelectionBytActiveTab();

    if (!selectionByActiveTab) {
      return;
    }

    if (selectionByActiveTab.has(channelId)) {
      selectionByActiveTab.delete(channelId);
    } else {
      if (!this.config?.isMultiselect) {
        selectionByActiveTab.clear();
      }

      selectionByActiveTab.add(channelId);
    }
  }

  toggleSelectAll() {
    if (this.isSelectedAll) {
      const selectionByActiveTab = this.getSelectionBytActiveTab();

      if (!selectionByActiveTab) {
        return;
      }

      selectionByActiveTab.clear();
    } else {
      this.selectedChannelIds.set(
        this.activeTab,
        new Set(this.selectableChannels.map(channel => channel._id)),
      );
    }
  }

  private get activeStore() {
    return this.channelStores.get(this.activeTab);
  }

  private get channels() {
    if (!this.isLoaded) {
      return [];
    }

    let result = this.activeStore?.items ?? [];

    result = this.processChannelsIfNeeded(result);

    return result;
  }

  private get disabledChannels() {
    return this.channels.filter(channel => this.isChannelDisabled(channel._id));
  }

  private get selectableChannels() {
    return this.channels.filter(
      channel => !this.isChannelDisabled(channel._id),
    );
  }

  private get shouldSearch() {
    return this.searchQuery.length >= 2;
  }

  private createStoreForTab(
    tab: ChannelVariant,
    factory: ChannelsStoreFactory,
  ) {
    switch (tab) {
      case 'course':
      case 'course_corporate':
      case 'course_individual':
      case 'course_individual_archived':
      case 'course_individual_corporate':
        return factory.createCourseChannelsStore({
          archived: tab.includes('archived'),
          corporate: tab.includes('corporate'),
          userId: currentUserStore.user?._id,
        });
      case 'dm':
        return factory.createDMChannelsStore(currentUserStore.user?._id);
      default:
        throw new Error(`Unknown tab: ${tab}`);
    }
  }

  private fillDisabledChannels(channels: Channel[]) {
    runInAction(() => {
      switch (this.activeTab) {
        case 'course_individual_archived':
          channels.forEach(channel => {
            if (
              !this.disabledChannelsIds.has(channel._id) &&
              isChannelReadonly(channel, channel.last_post)
            ) {
              this.disabledChannelsIds.add(channel._id);
            }
          });
          break;
        default:
          break;
      }
    });
  }

  private getSelectionBytActiveTab() {
    return this.selectedChannelIds.get(this.activeTab);
  }

  private isChannelDisabled(channelId: string) {
    return this.disabledChannelsIds.has(channelId);
  }

  private makeMessageField() {
    return createTextInputField('message', {
      validationRule: {
        max: 5000,
        messages: {
          stringMax: i18n.t('shared:validation.message_max_length', {
            maxLength: 5000,
          }),
        },
        optional: true,
        trim: true,
        type: 'string',
      },
    });
  }

  private processChannelsIfNeeded(channels: Channel[]) {
    let result = channels.slice();

    switch (this.activeTab) {
      case 'course':
        result = result.filter(channel => {
          const resource = channel.resource as Nullable<ChannelCourseResource>;

          // filter out channels with deleted clients but keep group channels
          return (
            (isDefined(resource?.client_id) && !resource.client.deleted) ||
            !isDefined(resource?.client_id)
          );
        });
        break;
      case 'course_individual':
      case 'course_individual_archived':
      case 'course_individual_corporate':
        result = result.filter(channel => {
          const resource = channel.resource as Nullable<ChannelCourseResource>;

          return isDefined(resource?.client_id) && !resource.client.deleted;
        });
        break;
      default:
        break;
    }

    if (this.config?.startedOnly) {
      result = result.filter(channel => {
        const resource = channel.resource as Nullable<ChannelCourseResource>;

        return resource ? isCourseStarted(resource) : true;
      });
    }

    if (this.shouldSearch) {
      result = findChannels(result, this.searchQuery, this.activeTab === 'dm');
    }

    return result;
  }

  private setInitialSelection() {
    if (this.config?.selectedChannelIds) {
      const isAnySelectedChannelExist = this.config.selectedChannelIds.some(
        id => this.selectableChannels.find(channel => channel._id === id),
      );

      if (isAnySelectedChannelExist) {
        this.selectedChannelIds.set(
          this.activeTab,
          new Set(this.config.selectedChannelIds),
        );
      }
    }
  }

  private setupReactions() {
    this.disposer.watch(
      'fill_disabled_channels',
      reaction(
        () => this.activeStore?.isLoaded,
        isLoaded => {
          if (isLoaded) {
            this.fillDisabledChannels(this.activeStore?.items ?? []);

            this.setInitialSelection();
          }
        },
      ),
    );
  }
}
